import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor } from 'ckeditor5';
import { MuiColorInput } from 'mui-color-input';
import { getEmptySlidingBanner } from '../../interfaces/SlidingBanner/SlidingBanner';
import {
  createSlidingBanner,
  deleteSlidingBanner,
  getSlidingBanner,
  updateSlidingBanner,
} from '../../api/SlidingBanner';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import NumberInput from '../../components/NumberInput/NumberInput';
import { RichTextEditor } from '../../components/RichTextEditor/RichTextEditor';
import { getFeURL } from '../../api/Settings';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';

export const ModifySlidingBannerPage = () => {
  const navigate = useNavigate();

  const editorRef = useRef<CKEditor<ClassicEditor>>(null);

  const { slidingBannerID: slidingBannerIDAsStr } = useParams<string>();

  const slidingBannerID = slidingBannerIDAsStr
    ? Number(slidingBannerIDAsStr)
    : undefined;
  const isEditing = Number.isFinite(slidingBannerID);

  const [slidingBanner, setSlidingBanner] = useState(getEmptySlidingBanner());
  const [feURL, setFeURL] = useState('');
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshSlidingBanner = () => {
    setLoading(true);
    getSlidingBanner(slidingBannerID!)
      .then(setSlidingBanner)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getFeURL()
      .then((url) => setFeURL(url))
      .catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    if (isEditing) {
      refreshSlidingBanner();
    }
  }, [slidingBannerIDAsStr]);

  const handleSave = () => {
    const body = editorRef.current?.editor?.getData() ?? '';
    const resultingSlidingBanner = { ...slidingBanner, body };

    if (isEditing) {
      setLoading(true);
      updateSlidingBanner(resultingSlidingBanner)
        .then((updatedSlidingBanner) => {
          toast.success('Kayan yazı başarıyla güncellendi.');
          setSlidingBanner(updatedSlidingBanner);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createSlidingBanner(resultingSlidingBanner)
        .then((createdSlidingBanner) => {
          toast.success('Kayan yazı başarıyla oluşturuldu.');
          navigate(`/visual/sliding-banners/${createdSlidingBanner.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteSlidingBanner(slidingBanner.id)
      .then(() => {
        toast.success('Kayan yazı başarıyla silindi.');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/visual/sliding-banners');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili kayan yazıyı silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Kayan Yazıyı Düzenle' : 'Yeni Kayan Yazı'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 900,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={slidingBanner.title}
          onChange={(e) =>
            setSlidingBanner({ ...slidingBanner, title: e.target.value })
          }
          fullWidth
        />
        <TextField
          label='Link'
          variant='outlined'
          size='small'
          value={slidingBanner.linkUrl}
          onChange={(e) =>
            setSlidingBanner({ ...slidingBanner, linkUrl: e.target.value })
          }
          fullWidth
          helperText={
            !!slidingBanner.linkUrl && !!feURL
              ? `${feURL}${slidingBanner.linkUrl}`
              : undefined
          }
        />

        <MuiColorInput
          format='hex'
          size='small'
          fullWidth
          value={slidingBanner.color}
          onChange={(newColor) =>
            setSlidingBanner({ ...slidingBanner, color: newColor })
          }
          label='Renk'
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant='h6'>Yazı</Typography>

          <TranslationAdornment
            richText
            defaultValue={slidingBanner.body}
            value={slidingBanner.bodyTranslation}
            onChange={(x) =>
              setSlidingBanner({ ...slidingBanner, bodyTranslation: x })
            }
            richTextProps={{ backgroundColor: slidingBanner.color }}
          />
        </Box>

        {!loading && (
          <RichTextEditor
            editorRef={editorRef}
            placeholder='Yazı İçeriği'
            initialData={slidingBanner.body}
            backgroundColor={slidingBanner.color}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            gap: 1.5,
          }}
        >
          <NumberInput
            label='Sıra'
            value={slidingBanner.sortOrder}
            onChange={(newOrder) =>
              setSlidingBanner({ ...slidingBanner, sortOrder: newOrder })
            }
          />
          <NumberInput
            label='Hız'
            value={slidingBanner.speed}
            onChange={(newSpeed) =>
              setSlidingBanner({ ...slidingBanner, speed: newSpeed })
            }
            endAdornment='Piksel/Saniye'
          />
          <NumberInput
            label='Aralık'
            value={slidingBanner.repeat}
            onChange={(newRepeat) =>
              setSlidingBanner({ ...slidingBanner, repeat: newRepeat })
            }
            endAdornment='Karakter'
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={slidingBanner.isActive}
                onChange={() =>
                  setSlidingBanner({
                    ...slidingBanner,
                    isActive: !slidingBanner.isActive,
                  })
                }
              />
            }
            label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
